import React from "react"
import {useQuery, useMutation, useSubscription} from "@apollo/client"

import MUTATION_JOB_START from "graphql/Transcode/Job/mutation/jobStart"
import MUTATION_JOB_STOP from "graphql/Transcode/Job/mutation/jobStop"
import JOB_SUBSCRIPTION from "graphql/Transcode/Job/subscription/jobUpdate"
import JOB_LIST_QUERY from "graphql/Transcode/Job/query/jobList"

import "./style.scss"

type JobListProps = {
  importId: string
}

const statusLabels: {[key: string]: string} = {
  INITIAL: "En attente",
  ERROR: "Erreur",
  DONE: "Terminé",
  RUNNING: "En cours",
}

export const JobList = (props: JobListProps) => {
  const {data, loading} = useQuery(JOB_LIST_QUERY, {
    skip: !props?.importId,
    variables: {id: props?.importId}
  })
  const jobList = data?.jobList || []
  const [startJobMutation] = useMutation(MUTATION_JOB_START)
  const [stopJobMutation] = useMutation(MUTATION_JOB_STOP)

  useSubscription(JOB_SUBSCRIPTION)

  const startJob = (jobId: string) => {
    startJobMutation({variables: {jobId}}).catch(() => undefined)
  }

  const stopJob = (jobId: string) => {
    stopJobMutation({variables: {jobId}}).catch(() => undefined)
  }

  const getEncodingDetails = (job: any) => {
    if (job.type !== "VIDEO" || !job.encodingOptions) {
      return ["Extraction de la piste", "Format source conservé"]
    }

    const {video, audio} = job.encodingOptions
    const videoLabel = video?.mode === "COPY"
      ? `Vidéo ${video.sourceCodec} copiée sans réencodage`
      : `Vidéo ${video.sourceCodec} vers ${video.targetCodec} · CRF ${video.crf} · ${video.preset}`
    const audioLabel = audio?.mode === "COPY"
      ? `${audio.streams?.length || 1} piste(s) audio copiée(s)`
      : `${audio.streams?.length || 1} piste(s) audio convertie(s) en AAC`
    const outputFormat = video?.outputWidth && video?.outputHeight
      ? `${video.outputWidth} × ${video.outputHeight} · ${video.hdr ? "HDR" : "SDR"}${video.frameRate ? ` · ${Number(video.frameRate).toFixed(3)} fps` : ""}`
      : "Définition source conservée"

    return [videoLabel, audioLabel, outputFormat]
  }

  if (loading && !jobList.length) {
    return <div className="jobs-loading"><span className="icon-spinner"></span> Préparation des tâches d'encodage…</div>
  }

  return <div className="job-list">
    <header className="job-list-header">
      <div>
        <span className="stage-eyebrow">Étape 4 sur 7</span>
        <h2>Transcodage en cours</h2>
        <p>{jobList.length} tâche{jobList.length > 1 ? "s" : ""} pour préparer ce média.</p>
      </div>
      <span className="job-count"><span className="fas fa-microchip" aria-hidden="true"></span>{jobList.length}</span>
    </header>

    <div className="job-stack">
      {jobList.map((job: any) => {
        const isMovie = !!job?.Import?.Media?.Movie
        const episode = job?.Import?.Media?.Episode
        const element = isMovie
          ? job?.Import?.Media?.Movie?.name
          : `${episode?.Season?.Show?.name || "Série"} · S${episode?.Season?.number || "–"} E${episode?.number || "–"} · ${episode?.name || "Épisode"}`
        const isStartable = ["INITIAL", "ERROR", "DONE"].includes(job?.status)
        const isRunning = job?.status === "RUNNING"
        const progress = Math.max(0, Math.min(100, Number(job?.progress) || 0))
        const encodingDetails = getEncodingDetails(job)
        const statusClass = String(job?.status || "INITIAL").toLowerCase()

        return <article className={`job-card job-${statusClass}`} key={`job_${job?.id}`}>
          <div className="job-card-topline">
            <span className="job-type-icon fas fa-film" aria-hidden="true"></span>
            <div className="job-title-block">
              <h3>{element}</h3>
              <div className="job-tags"><span>{job?.type || "MEDIA"}</span><span>{job?.language || "und"}</span></div>
            </div>
            <span className={`job-status job-status-${statusClass}`}>
              {isRunning && <span className="status-pulse" aria-hidden="true"></span>}
              {statusLabels[job?.status] || job?.status}
            </span>
          </div>

          <div className="job-card-content">
            <div className="encoding-summary">
              <span className="encoding-label">Profil de sortie</span>
              <strong>{encodingDetails[0]}</strong>
              {encodingDetails.slice(1).map((detail: string) => <span key={detail}>{detail}</span>)}
            </div>
            <div className="job-progress-panel">
              <div className="job-progress-copy"><span>Progression</span><strong>{Math.round(progress)}<small>%</small></strong></div>
              <div className="job-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
                <span style={{width: `${progress}%`}}></span>
              </div>
              <div className="job-progress-footer">
                <span>{progress >= 100 ? "Encodage terminé" : isRunning ? "Traitement des données…" : "En attente de traitement"}</span>
                <div className="job-actions">
                  {isStartable && <button className="job-start-button" onClick={() => startJob(job?.id)}><span className="fas fa-play"></span> Démarrer</button>}
                  {isRunning && <button className="job-stop-button" onClick={() => stopJob(job?.id)}><span className="fas fa-stop"></span> Arrêter</button>}
                </div>
              </div>
            </div>
          </div>
        </article>
      })}
    </div>

    {!loading && !jobList.length && <div className="jobs-empty">
      <span className="fas fa-cogs" aria-hidden="true"></span>
      <strong>Aucune tâche générée</strong>
      <span>Les encodages apparaîtront ici dès qu'ils seront prêts.</span>
    </div>}
  </div>
}
